import { useState, useRef } from "react";
import { useProfileVideos, ProfileVideo } from "@/hooks/useProfileVideos";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Video,
  Upload,
  Trash2,
  Eye,
  EyeOff,
  Play,
  Pause,
  Plus,
  Lock,
  Unlock,
  Loader2,
} from "lucide-react";

const ProfileVideosManager = () => {
  const { 
    videos, 
    loading, 
    togglePreMatchVisibility, 
    uploadVideo, 
    deleteVideo,
    preMatchCount,
  } = useProfileVideos();
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProfileVideo | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    
    // Check video duration
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = async () => {
      window.URL.revokeObjectURL(video.src);
      
      if (video.duration > 15) {
        setUploading(false);
        return;
      }

      // Auto-set as pre-match visible if we have less than 2
      const makeVisible = preMatchCount < 2;
      await uploadVideo(file, makeVisible);
      setUploading(false);
    };

    video.onerror = () => {
      setUploading(false);
    };

    video.src = URL.createObjectURL(file);
    e.target.value = "";
  };

  const handleToggleVisibility = async (video: ProfileVideo) => {
    await togglePreMatchVisibility(video.id, !video.visible_before_match);
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteVideo(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const togglePlay = (videoId: string, videoEl: HTMLVideoElement) => {
    if (playingId === videoId) {
      videoEl.pause();
      setPlayingId(null);
    } else {
      // Pause any currently playing
      document.querySelectorAll("video").forEach(v => v.pause());
      videoEl.play();
      setPlayingId(videoId);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-4">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Video className="w-5 h-5 text-primary" />
          Profile Videos
        </h3>
        <Badge variant="secondary" className="text-xs">
          {preMatchCount}/2 visible before match
        </Badge>
      </div>

      {/* Info text */}
      <p className="text-sm text-muted-foreground">
        Choose which videos are visible before you match. After matching, all your videos become visible.
      </p>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Video grid */}
      {videos.length === 0 ? (
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full p-6 border-2 border-dashed border-border rounded-xl flex flex-col items-center gap-3 hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
        >
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            {uploading ? (
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            ) : (
              <Upload className="w-8 h-8 text-primary" />
            )}
          </div>
          <div className="text-center">
            <p className="font-semibold">Add Your First Video</p>
            <p className="text-sm text-muted-foreground">
              Record a short intro (max 15 seconds)
            </p>
          </div>
        </button>
      ) : (
        <div className="space-y-3">
          {videos.map((video) => (
            <div 
              key={video.id} 
              className="flex gap-3 p-3 rounded-xl bg-muted/50 border border-border"
            >
              {/* Video thumbnail */}
              <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-black flex-shrink-0">
                <video
                  src={video.video_url || `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/profile-videos/${video.storage_path}`}
                  className="w-full h-full object-cover"
                  playsInline
                  muted
                  onEnded={() => setPlayingId(null)}
                />
                <button
                  onClick={(e) => {
                    const videoEl = e.currentTarget.previousElementSibling as HTMLVideoElement;
                    togglePlay(video.id, videoEl);
                  }}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                >
                  {playingId === video.id ? (
                    <Pause className="w-6 h-6 text-white" />
                  ) : (
                    <Play className="w-6 h-6 text-white" />
                  )}
                </button>
              </div>

              {/* Video info and controls */}
              <div className="flex-1 flex flex-col justify-between min-w-0">
                <div className="flex items-center gap-2">
                  {video.visible_before_match ? (
                    <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-xs gap-1">
                      <Unlock className="w-3 h-3" />
                      Visible to all
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs gap-1">
                      <Lock className="w-3 h-3" />
                      Match only
                    </Badge>
                  )}
                  {video.duration_seconds && (
                    <span className="text-xs text-muted-foreground">
                      {Math.round(video.duration_seconds)}s
                    </span>
                  )}
                </div>

                {/* Toggle and delete */}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`visible-${video.id}`}
                      checked={video.visible_before_match}
                      onCheckedChange={() => handleToggleVisibility(video)}
                      className="scale-90"
                    />
                    <Label 
                      htmlFor={`visible-${video.id}`} 
                      className="text-xs text-muted-foreground cursor-pointer"
                    >
                      {video.visible_before_match ? (
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" /> Before match
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <EyeOff className="w-3 h-3" /> After match only
                        </span>
                      )}
                    </Label>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setDeleteTarget(video)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {/* Add more button */}
          <Button
            variant="outline"
            className="w-full"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Add Another Video
          </Button>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Video?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this video from your profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProfileVideosManager;
